import { type CreateValveRequestBody, type ReconcileValveUpdateItem, type Valve } from "@eisox/api/core-api";
import { validateValveMac } from "@eisox/valves";
import { z } from "@eisox/zod";

import { m } from "~/paraglide/messages";

export type ValveMode = "install" | "preinstall";

export interface ValveFormValues {
  /** Client-only id identifying the current/staged valve (for edit-in-place). */
  id: string;
  /** Client-only: which floor plan the valve is placed on (its room lives on this floor). */
  floorId: string;
  roomId: string;
  gatewayId: string;
  /** "" = no heating network. */
  heatingNetworkId: string;
  uid: number;
  mac: string;
  x: number;
  y: number;
}

/** A valve staged on the plan, awaiting save. `serverId` set = an existing planned valve being edited. */
export interface StagedValve extends ValveFormValues {
  serverId?: string;
}

/** Next free valve number: one past the highest uid across existing and already-staged valves. */
export const nextValveUid = (valves: Valve[], staged: StagedValve[]): number => {
  const uids = [...valves.map(v => v.uid), ...staged.map(s => s.uid)];
  return uids.length > 0 ? Math.max(...uids) + 1 : 1;
};

/** A blank current valve on `floorId` for `uid` (room/gateway/serial to be filled in). */
export const emptyValveForm = (id: string, floorId: string, uid: number): ValveFormValues => ({
  id,
  floorId,
  roomId: "",
  gatewayId: "",
  heatingNetworkId: "",
  uid,
  mac: "",
  x: 0,
  y: 0,
});

/** Load an existing planned (no-mac) valve into a staged valve tagged with its `serverId`. */
export const plannedValveToStaged = (valve: Valve): StagedValve => ({
  id: valve.id,
  serverId: valve.id,
  floorId: valve.floor.id,
  roomId: valve.roomId,
  gatewayId: valve.gatewayId,
  heatingNetworkId: valve.heatingNetworkId ?? "",
  uid: valve.uid,
  mac: "",
  x: valve.floor.x,
  y: valve.floor.y,
});

/**
 * Validates one staged valve. `getTakenMacs` is a getter so the (stable) schema always reads the live
 * serials as valves are staged. In install mode the serial is required, format-checked and unique; in
 * pre-install it is absent. `uid` must be unique among `existingUids` and other staged valves
 * (`data.id` excluded). Room and gateway are required; the heating network is optional.
 */
export const valveFormSchema = (
  mode: ValveMode,
  getTakenMacs: () => string[],
  staged: Pick<StagedValve, "id" | "uid">[],
  existingUids: number[],
) =>
  z
    .object({
      id: z.string(),
      floorId: z.string(),
      roomId: z.string().nonempty(m.valve_add_room_required()),
      gatewayId: z.string().nonempty(m.valve_add_gateway_required()),
      heatingNetworkId: z.string(),
      uid: z.coerce.number<number>().int().positive(),
      mac: z.string(),
      x: z.number(),
      y: z.number(),
    })
    .superRefine((data, ctx) => {
      if (mode === "install") {
        if (!validateValveMac(data.mac)) {
          ctx.addIssue({ path: ["mac"], code: "custom", message: m.serial_number_invalid() });
        } else if (getTakenMacs().some(mac => mac.toLowerCase() === data.mac.toLowerCase())) {
          ctx.addIssue({ path: ["mac"], code: "custom", message: m.serial_number_taken() });
        }
      }
      const takenUids = [...existingUids, ...staged.filter(s => s.id !== data.id).map(s => s.uid)];
      if (takenUids.includes(data.uid)) {
        ctx.addIssue({ path: ["uid"], code: "custom", message: m.valve_uid_unique() });
      }
    });

/** Whether a staged valve's editable fields differ from its loaded original (drives PATCH vs no-op). */
export const valveDiffers = (a: StagedValve, b: StagedValve): boolean =>
  a.roomId !== b.roomId ||
  a.gatewayId !== b.gatewayId ||
  a.heatingNetworkId !== b.heatingNetworkId ||
  a.uid !== b.uid ||
  a.x !== b.x ||
  a.y !== b.y;

export const stagedToCreateBody = (staged: StagedValve, mode: ValveMode): CreateValveRequestBody => ({
  roomId: staged.roomId,
  uid: staged.uid,
  gatewayId: staged.gatewayId,
  x: staged.x,
  y: staged.y,
  ...(mode === "install" && staged.mac ? { mac: staged.mac } : {}),
  ...(staged.heatingNetworkId ? { heatingNetworkId: staged.heatingNetworkId } : {}),
});

export const stagedToUpdateBody = (staged: StagedValve): Omit<ReconcileValveUpdateItem, "id"> => ({
  roomId: staged.roomId,
  uid: staged.uid,
  gatewayId: staged.gatewayId,
  x: staged.x,
  y: staged.y,
  ...(staged.heatingNetworkId ? { heatingNetworkId: staged.heatingNetworkId } : {}),
});
